/**
 * WalletToken — one Apple Wallet web-service bearer per ticket, at
 * `workspaces/{orgId}/events/{eventId}/walletTokens/{ticketId}`. Declares
 * zero actions — no `read`, no `write` of any kind — so the emitted rules
 * carry no `match` block for this path at all and the default-deny tail
 * (`match /{document=**} { allow read, write: if false; }`) covers it.
 *
 * A sibling default-denied doc rather than a field on Ticket
 * (`ticket.entity.ts`): the `tickets.checkin.scan` role reads/lists whole
 * Ticket documents and Firestore has no field-level ACL, so a bearer carried
 * on Ticket itself would reach every scanner account. The same containment
 * pattern `integration.entity.ts` uses for credential values is what
 * actually keeps it out of the scan role's reach: only the Admin SDK —
 * `walletApple.api.ts`'s own mint, and `walletWebService.webhook.ts`'s own
 * validation seam — ever reads or writes it.
 *
 * Sibling to Ticket by ticket id, not a field on it: same id, different
 * document, so the scan role's whole-doc grant on Ticket never reaches this
 * one.
 *
 * `ticketId` duplicates this doc's own id as a field — earned, not
 * decorative: `walletWebService.webhook.ts`'s four REST endpoints receive only a
 * serial number (Apple's URL segment), never an orgId/eventId, and a
 * Firestore collection-group query can filter on a field value with no
 * parent path known, but `FieldPath.documentId()` in a collection-group
 * query requires the FULL path, which is exactly the unknown this field
 * resolves — `db.collectionGroup('walletTokens').where('ticketId', '==',
 * serial)` is how the web service (and nothing else) recovers which
 * workspace/event this serial belongs to.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const WalletTokenSchema = z.object({
	ticketId: z
		.string()
		.min(1)
		.describe(
			"this doc's own id, restated as a field — the only way walletWebService.webhook.ts's collection-group query can resolve a bare serial number back to its owning workspace/event",
		),
	authenticationToken: z
		.string()
		.min(1)
		.describe(
			"this ticket's own signed .pkpass web-service bearer — the value walletApple.api.ts mints and embeds as the pass's authenticationToken, and what walletWebService.webhook.ts validates every `Authorization: ApplePass {token}` request against",
		),
});
export type WalletToken = z.infer<typeof WalletTokenSchema>;

export const WalletToken = entity<Actor>()({
	schema: WalletTokenSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/walletTokens/{ticketId}",
		}),
	);
